import * as THREE from 'three';

const CARDINALS=[
  [1,0,1],[-1,0,1],[0,1,1],[0,-1,1],
  [1,1,Math.SQRT2],[1,-1,Math.SQRT2],[-1,1,Math.SQRT2],[-1,-1,Math.SQRT2],
];

function obstacleBlocksCars(obstacle){
  return obstacle.minY<.9&&obstacle.maxY>.04;
}

function pointHitsObstacle(x,z,obstacle,margin){
  if(obstacle.shape==='circle'){
    const dx=x-obstacle.x;
    const dz=z-obstacle.z;
    const radius=obstacle.radius+margin;
    return dx*dx+dz*dz<radius*radius;
  }
  return x>obstacle.minX-margin&&x<obstacle.maxX+margin
    &&z>obstacle.minZ-margin&&z<obstacle.maxZ+margin;
}

class MinHeap{
  constructor(){this.items=[];}

  push(node){
    const items=this.items;
    items.push(node);
    let index=items.length-1;
    while(index>0){
      const parent=(index-1)>>1;
      if(items[parent].score<=node.score)break;
      items[index]=items[parent];
      index=parent;
    }
    items[index]=node;
  }

  pop(){
    const items=this.items;
    if(!items.length)return null;
    const first=items[0];
    const last=items.pop();
    if(items.length&&last){
      let index=0;
      while(true){
        const left=index*2+1;
        const right=left+1;
        if(left>=items.length)break;
        const child=right<items.length&&items[right].score<items[left].score?right:left;
        if(items[child].score>=last.score)break;
        items[index]=items[child];
        index=child;
      }
      items[index]=last;
    }
    return first;
  }
}

export class GridNavigator{
  constructor(world,{cellSize=2.4,clearance=.88}={}){
    this.world=world;
    this.cellSize=cellSize;
    this.clearance=clearance;
    this.columns=Math.max(3,Math.ceil(world.width/cellSize));
    this.rows=Math.max(3,Math.ceil(world.depth/cellSize));
    this.halfWidth=world.width/2;
    this.halfDepth=world.depth/2;
    this.blockingObstacles=world.obstacles.filter(obstacleBlocksCars);
    this.walkable=new Uint8Array(this.columns*this.rows);
    for(let z=0;z<this.rows;z+=1){
      for(let x=0;x<this.columns;x+=1){
        const point=this.cellToWorld(x,z);
        this.walkable[this.index(x,z)]=this.isPointWalkable(point.x,point.z)?1:0;
      }
    }
  }

  index(x,z){return z*this.columns+x;}

  coordinates(index){
    return {x:index%this.columns,z:Math.floor(index/this.columns)};
  }

  containsCell(x,z){return x>=0&&z>=0&&x<this.columns&&z<this.rows;}

  cellToWorld(x,z){
    return new THREE.Vector3(
      -this.halfWidth+(x+.5)*this.cellSize,
      0,
      -this.halfDepth+(z+.5)*this.cellSize,
    );
  }

  worldToCell(point){
    return {
      x:THREE.MathUtils.clamp(Math.floor((point.x+this.halfWidth)/this.cellSize),0,this.columns-1),
      z:THREE.MathUtils.clamp(Math.floor((point.z+this.halfDepth)/this.cellSize),0,this.rows-1),
    };
  }

  isPointWalkable(x,z,extraMargin=0){
    const margin=this.clearance+extraMargin;
    if(x<=-this.halfWidth+margin||x>=this.halfWidth-margin||z<=-this.halfDepth+margin||z>=this.halfDepth-margin)return false;
    return !this.blockingObstacles.some((obstacle)=>pointHitsObstacle(x,z,obstacle,margin));
  }

  isCellWalkable(x,z){
    return this.containsCell(x,z)&&this.walkable[this.index(x,z)]===1;
  }

  nearestWalkableCell(point,maxRadius=12){
    const origin=this.worldToCell(point);
    if(this.isCellWalkable(origin.x,origin.z))return origin;
    let best=null;
    let bestDistance=Infinity;
    for(let radius=1;radius<=maxRadius;radius+=1){
      for(let dz=-radius;dz<=radius;dz+=1){
        for(let dx=-radius;dx<=radius;dx+=1){
          if(Math.abs(dx)!==radius&&Math.abs(dz)!==radius)continue;
          const x=origin.x+dx;
          const z=origin.z+dz;
          if(!this.isCellWalkable(x,z))continue;
          const candidate=this.cellToWorld(x,z);
          const distance=candidate.distanceToSquared(point);
          if(distance<bestDistance){bestDistance=distance;best={x,z};}
        }
      }
      if(best)return best;
    }
    return null;
  }

  nearestWalkablePoint(point){
    if(this.isPointWalkable(point.x,point.z))return new THREE.Vector3(point.x,0,point.z);
    const cell=this.nearestWalkableCell(point);
    return cell?this.cellToWorld(cell.x,cell.z):null;
  }

  hasLineOfSight(from,to){
    const distance=Math.hypot(to.x-from.x,to.z-from.z);
    const steps=Math.max(1,Math.ceil(distance/(this.cellSize*.42)));
    for(let i=1;i<=steps;i+=1){
      const t=i/steps;
      if(!this.isPointWalkable(
        THREE.MathUtils.lerp(from.x,to.x,t),
        THREE.MathUtils.lerp(from.z,to.z,t),
        -.08,
      ))return false;
    }
    return true;
  }

  findRandomPoint(random,attempts=48){
    for(let attempt=0;attempt<attempts;attempt+=1){
      const point=new THREE.Vector3(
        THREE.MathUtils.lerp(-this.halfWidth+2,this.halfWidth-2,random()),
        0,
        THREE.MathUtils.lerp(-this.halfDepth+2,this.halfDepth-2,random()),
      );
      if(this.isPointWalkable(point.x,point.z,.35))return point;
    }
    return null;
  }

  findPath(start,goal){
    const startCell=this.nearestWalkableCell(start);
    const goalCell=this.nearestWalkableCell(goal);
    if(!startCell||!goalCell)return [];
    const startIndex=this.index(startCell.x,startCell.z);
    const goalIndex=this.index(goalCell.x,goalCell.z);
    if(startIndex===goalIndex)return [this.nearestWalkablePoint(goal)||this.cellToWorld(goalCell.x,goalCell.z)];

    const count=this.walkable.length;
    const scores=new Float64Array(count);scores.fill(Infinity);
    const previous=new Int32Array(count);previous.fill(-1);
    const closed=new Uint8Array(count);
    const heap=new MinHeap();
    scores[startIndex]=0;
    heap.push({index:startIndex,cost:0,score:0});
    let iterations=0;

    while(heap.items.length&&iterations<count*3){
      iterations+=1;
      const current=heap.pop();
      if(!current||closed[current.index]||current.cost!==scores[current.index])continue;
      if(current.index===goalIndex)break;
      closed[current.index]=1;
      const {x,z}=this.coordinates(current.index);
      for(const [dx,dz,cost] of CARDINALS){
        const nextX=x+dx;
        const nextZ=z+dz;
        if(!this.isCellWalkable(nextX,nextZ))continue;
        if(dx&&dz&&(!this.isCellWalkable(x+dx,z)||!this.isCellWalkable(x,z+dz)))continue;
        const nextIndex=this.index(nextX,nextZ);
        if(closed[nextIndex])continue;
        const nextCost=current.cost+cost;
        if(nextCost>=scores[nextIndex])continue;
        scores[nextIndex]=nextCost;
        previous[nextIndex]=current.index;
        const differenceX=Math.abs(goalCell.x-nextX);
        const differenceZ=Math.abs(goalCell.z-nextZ);
        const heuristic=Math.max(differenceX,differenceZ)+(Math.SQRT2-1)*Math.min(differenceX,differenceZ);
        heap.push({index:nextIndex,cost:nextCost,score:nextCost+heuristic});
      }
    }

    if(previous[goalIndex]===-1)return [];
    const indices=[];
    for(let current=goalIndex;current!==-1&&current!==startIndex;current=previous[current])indices.push(current);
    indices.reverse();
    const points=indices.map((index)=>{
      const {x,z}=this.coordinates(index);
      return this.cellToWorld(x,z);
    });
    const exactGoal=this.nearestWalkablePoint(goal);
    if(exactGoal&&points.length&&this.hasLineOfSight(points[points.length-1],exactGoal))points[points.length-1]=exactGoal;
    return this.simplifyPath(start,points);
  }

  simplifyPath(start,points){
    if(points.length<2)return points;
    const simplified=[];
    let anchor=start;
    let index=0;
    while(index<points.length){
      let furthest=index;
      for(let candidate=points.length-1;candidate>index;candidate-=1){
        if(this.hasLineOfSight(anchor,points[candidate])){furthest=candidate;break;}
      }
      simplified.push(points[furthest]);
      anchor=points[furthest];
      index=furthest+1;
    }
    return simplified;
  }
}
